import { useState, useEffect, useCallback } from 'react';
import { usePractice, useStartInstantVisit, useScheduleVisit, useEditVisit, useCancelVisit, useEndSmsVisit } from '../api/queries';
import { trackVisitCreated } from '../utils/analytics';
import { useHeadingActions } from '../components/layout/AppShell';
import { CreateVisitDropdowns } from '../components/visits/CreateVisitDropdowns';
import { InstantVisitModal } from '../components/visits/InstantVisitModal';
import { ScheduleVisitModal } from '../components/visits/ScheduleVisitModal';
import { EditVisitModal } from '../components/visits/EditVisitModal';
import { VisitDetailModal } from '../components/visits/VisitDetailModal';
import { ConfirmModal } from '../components/visits/ConfirmModal';
import { apiCall } from '../api/client';
import type { Visit } from '../types';

export function usePracticeInfo() {
  const { data: practiceData } = usePractice();
  const subscription = practiceData?.practice?.subscription;
  const status = subscription?.status || 'none';
  return {
    practiceData,
    hasActivePlan: status === 'active' || status === 'trialing',
    defaultDuration: String(practiceData?.practice?.settings?.defaultVisitDuration || 30),
    buttonsDisabled: status === 'none' || status === 'canceled',
    subscription,
    status,
  };
}

export function useVisitModals() {
  const { defaultDuration, buttonsDisabled } = usePracticeInfo();

  const [instantModal, setInstantModal] = useState<{ open: boolean; medium: string; error?: string | null }>({ open: false, medium: 'video' });
  const [scheduleModal, setScheduleModal] = useState<{ open: boolean; medium: string }>({ open: false, medium: 'video' });
  const [editModal, setEditModal] = useState<{ open: boolean; visit: Visit | null }>({ open: false, visit: null });
  const [detailModal, setDetailModal] = useState<{ open: boolean; visit: Visit | null }>({ open: false, visit: null });
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; visit: Visit | null; type: string }>({ open: false, visit: null, type: '' });
  const instantVisit = useStartInstantVisit();
  const scheduleVisit = useScheduleVisit();
  const editVisit = useEditVisit();
  const cancelVisit = useCancelVisit();
  const endSmsVisit = useEndSmsVisit();

  const handleInstant = useCallback((medium: string) => {
    setInstantModal({ open: true, medium, error: null });
  }, []);

  const handleSchedule = useCallback((medium: string) => {
    setScheduleModal({ open: true, medium });
  }, []);

  const openDetail = useCallback((visit: Visit) => setDetailModal({ open: true, visit }), []);

  // Inject create-visit dropdowns into the AppShell heading bar
  const { setActions } = useHeadingActions();
  useEffect(() => {
    setActions(
      <CreateVisitDropdowns onInstant={handleInstant} onSchedule={handleSchedule} disabled={buttonsDisabled} />
    );
    return () => setActions(null);
  }, [setActions, handleInstant, handleSchedule, buttonsDisabled]);

  const modals = (
    <>
      <InstantVisitModal
        open={instantModal.open}
        medium={instantModal.medium}
        error={instantModal.error}
        onClose={() => setInstantModal({ open: false, medium: instantModal.medium, error: null })}
        onConfirm={async (data) => {
          const medium = instantModal.medium;
          try {
            const result = await instantVisit.mutateAsync({
              medium,
              patientName: data.patientName,
              patientEmail: data.patientEmail,
              patientPhone: data.patientPhone,
              smsConsent: data.smsConsent ? { source: 'provider_attestation', consentedAt: Date.now() } : null,
            });
            setInstantModal({ open: false, medium, error: null });
            trackVisitCreated(medium, 'instant');
            // Open visit detail modal with the created visit
            if (result) {
              setDetailModal({ open: true, visit: {
                ...result,
                medium,
                patientName: data.patientName,
                patientEmail: data.patientEmail,
                patientPhone: data.patientPhone,
                isSmsVisit: medium === 'sms',
              } as Visit });
            }
          } catch (err) {
            setInstantModal({ open: true, medium, error: err instanceof Error ? err.message : 'Failed to create visit. Please try again.' });
          }
        }}
      />
      <ScheduleVisitModal
        open={scheduleModal.open}
        medium={scheduleModal.medium}
        defaultDuration={defaultDuration}
        onClose={() => setScheduleModal({ open: false, medium: scheduleModal.medium })}
        onConfirm={async (body) => {
          setScheduleModal({ open: false, medium: scheduleModal.medium });
          await scheduleVisit.mutateAsync(body);
          trackVisitCreated(scheduleModal.medium, 'scheduled');
        }}
      />
      <EditVisitModal
        visit={editModal.visit}
        open={editModal.open}
        onClose={() => {
          const visit = editModal.visit;
          setEditModal({ open: false, visit: null });
          if (visit) setDetailModal({ open: true, visit });
        }}
        onSave={async (body) => {
          if (editModal.visit) {
            await editVisit.mutateAsync({ visit: editModal.visit, body });
            setEditModal({ open: false, visit: null });
          }
        }}
      />
      <VisitDetailModal
        visit={detailModal.visit}
        open={detailModal.open}
        onClose={() => setDetailModal({ open: false, visit: null })}
        onEdit={(v) => { setDetailModal({ open: false, visit: null }); setEditModal({ open: true, visit: v }); }}
        onCancel={(v) => { setDetailModal({ open: false, visit: null }); setConfirmModal({ open: true, visit: v, type: 'cancel' }); }}
        onSmsEnd={(v) => { setDetailModal({ open: false, visit: null }); setConfirmModal({ open: true, visit: v, type: 'smsEnd' }); }}
      />
      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.type === 'smsEnd' ? 'End SMS Visit' : 'Cancel Visit'}
        message={confirmModal.type === 'smsEnd' ? 'End this SMS visit? The patient will be notified.' : 'Cancel this visit? The patient will be notified.'}
        actionLabel={confirmModal.type === 'smsEnd' ? 'End Visit' : 'Cancel Visit'}
        onCancel={() => {
          const visit = confirmModal.visit;
          setConfirmModal({ open: false, visit: null, type: '' });
          if (visit) setDetailModal({ open: true, visit });
        }}
        onConfirm={async () => {
          if (confirmModal.visit) {
            if (confirmModal.type === 'smsEnd') await endSmsVisit.mutateAsync(confirmModal.visit.visitId!);
            else await cancelVisit.mutateAsync(confirmModal.visit);
          }
          setConfirmModal({ open: false, visit: null, type: '' });
        }}
      />
    </>
  );

  return { openDetail, modals };
}
